import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { fetchList } from "../api";
import { PageHero, Loader } from "../components/Shared";

export const extractYouTubeId = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (!s.includes("http") && !s.includes("/") && !s.includes("=")) return s;
  const m = s.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : s;
};

export default function Media() {
  const [videos, setVideos] = useState(null);
  const [active, setActive] = useState(0);
  const [titles, setTitles] = useState({});

  useEffect(() => { fetchList("videos").then(setVideos).catch(() => setVideos([])); }, []);
  useEffect(() => {
    if (!videos) return;
    videos.forEach((v) => {
      if (v.title) return;
      const id = extractYouTubeId(v.youtube_id);
      setTitles((prev) => {
        if (prev[id]) return prev;
        fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${id}`)
          .then((r) => r.json())
          .then((d) => { if (d && d.title) setTitles((t) => ({ ...t, [id]: d.title })); })
          .catch(() => {});
        return prev;
      });
    });
  }, [videos]);

  if (!videos) return <Loader />;

  const displayTitle = (v) => v.title || titles[extractYouTubeId(v.youtube_id)] || "";
  const featured = videos[active];
  const featuredId = featured ? extractYouTubeId(featured.youtube_id) : "";

  return (
    <div data-testid="media-page">
      <PageHero kicker="Watch CWF" title="Media" image="https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="px-6 md:px-12 lg:px-20 pb-24">
        {videos.length === 0 ? (
          <p className="text-zinc-500">Videos coming soon.</p>
        ) : (
          <>
            {/* Featured player */}
            <div className="mb-12" data-testid="media-featured">
              <div className="aspect-video w-full bg-black border border-zinc-800">
                <iframe
                  key={featuredId}
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${featuredId}?rel=0`}
                  title={featured.title || "CWF Video"}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              {featured && displayTitle(featured) && <h2 className="font-display text-3xl uppercase tracking-tight mt-4">{displayTitle(featured)}</h2>}
            </div>

            {/* Playlist grid */}
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[2px] bg-brand" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">All Videos</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {videos.map((v, i) => {
                const id = extractYouTubeId(v.youtube_id);
                return (
                  <motion.button
                    key={v.id}
                    data-testid={`media-thumb-${i}`}
                    onClick={() => { setActive(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (i % 6) * 0.05 }}
                    className={`group text-left border overflow-hidden transition-colors ${i === active ? "border-brand" : "border-zinc-800 hover:border-zinc-600"}`}
                  >
                    <div className="relative aspect-video overflow-hidden bg-black">
                      <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt={v.title || "CWF video"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="w-14 h-14 bg-brand text-black flex items-center justify-center"><Play size={24} fill="currentColor" /></span>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-display text-lg uppercase tracking-tight leading-none">{displayTitle(v) || "CWF Video"}</p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
